import { Alert, AlertDescription, AlertTitle } from "@coval/ui/alert"
import { Button } from "@coval/ui/button"
import { Card, CardContent } from "@coval/ui/card"
import { FormActions, FormField } from "@coval/ui/form-field"
import { Input } from "@coval/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui/select"
import { SettingRow } from "@coval/ui/setting-row"
import { Skeleton } from "@coval/ui/skeleton"
import { Switch } from "@coval/ui/switch"

import { SaveResourceButton } from "@/components/ResourceActionButton"

import {
  useOverageSettingsEditor,
  type OverageSettingsPort,
} from "../hooks/useOverageSettingsEditor"
import {
  currencyFractionDigits,
  minorAmountInput,
} from "../utils/overageSettings"

interface OverageSettingsPanelProps {
  organizationId: string
  port: OverageSettingsPort
}

const LIMIT_DESCRIPTIONS = {
  unset:
    "No spending limit has been selected. This does not mean unlimited spending.",
  unlimited: "The recorded preference has no monetary limit.",
  limited: "Zero is a valid limit and records no additional overage budget.",
}

function OverageSettingsEditor({
  organizationId,
  port,
}: OverageSettingsPanelProps) {
  const editor = useOverageSettingsEditor(organizationId, port)
  const { data, draft, feedback } = editor
  const controls = data?.controls
  const fractionDigits = controls
    ? currencyFractionDigits(controls.currency)
    : null
  const usesMinorUnits = fractionDigits === null

  return (
    <Card>
      <CardContent className="space-y-5 p-6">
        <div className="space-y-1">
          <h2 className="text-sm font-semibold text-coval-text">
            Overage settings
          </h2>
          <p className="text-sm text-coval-text-muted">
            Record your overage preference and spending limit. Saving these
            settings does not activate charging or enforce a spending stop.
          </p>
        </div>
        {feedback === "load-error" && (
          <Alert variant="danger">
            <AlertTitle>Couldn't load current settings</AlertTitle>
            <AlertDescription>
              Your draft is preserved. Retry before saving.
            </AlertDescription>
            <Button
              variant="secondary"
              className="mt-3"
              disabled={editor.isReading}
              onClick={() => void editor.load()}
            >
              Retry loading settings
            </Button>
          </Alert>
        )}
        {data === null && feedback !== "load-error" && (
          <div
            role="status"
            aria-label="Loading overage settings"
            className="space-y-3"
          >
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}
        {data !== null && !data.configured && (
          <Alert variant="info">
            <AlertTitle>Commercial enrollment required</AlertTitle>
            <AlertDescription>
              This organization has no enrolled overage settings. Contact your
              Coval account team to configure commercial terms and currency
              before editing these controls.
            </AlertDescription>
            <Button
              variant="secondary"
              className="mt-3"
              disabled={editor.isReading}
              onClick={() => void editor.load()}
            >
              Check enrollment again
            </Button>
          </Alert>
        )}
        {controls && draft && (
          <form
            role="presentation"
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault()
              void editor.save()
            }}
            onKeyDown={(event) => {
              if (
                event.key === "Enter" &&
                event.target instanceof HTMLInputElement &&
                editor.validationIssues.length > 0
              ) {
                event.preventDefault()
                editor.revealValidation()
              }
            }}
          >
            <div className="rounded-md bg-surface-1 p-3 text-sm text-coval-text-muted">
              <p>
                Enrolled currency:{" "}
                <span className="font-medium text-coval-text">
                  {controls.currency}
                </span>
              </p>
              <p>
                Saved preference:{" "}
                {controls.overages_enabled ? "enabled" : "disabled"} ·{" "}
                {controls.limit_state === "limited" &&
                controls.limit_minor !== null
                  ? `${controls.currency} ${minorAmountInput(controls.limit_minor, controls.currency)}${usesMinorUnits ? " minor units" : ""}`
                  : controls.limit_state === "unset"
                    ? "limit not set"
                    : "unlimited"}
              </p>
            </div>
            <SettingRow
              label="Overages enabled"
              description="Disabling this preference preserves the selected limit."
            >
              {(id, describedBy) => (
                <Switch
                  id={id}
                  {...describedBy}
                  checked={draft.overagesEnabled}
                  disabled={editor.blocked}
                  onCheckedChange={(overagesEnabled) =>
                    editor.changeDraft({ ...draft, overagesEnabled })
                  }
                />
              )}
            </SettingRow>
            <SettingRow
              label="Spending limit"
              stacked
              description={LIMIT_DESCRIPTIONS[draft.limitState]}
            >
              {(id, describedBy) => (
                <Select
                  value={draft.limitState}
                  disabled={editor.blocked}
                  onValueChange={(value) => {
                    if (
                      value === "unset" ||
                      value === "unlimited" ||
                      value === "limited"
                    ) {
                      editor.changeDraft({ ...draft, limitState: value })
                    }
                  }}
                >
                  <SelectTrigger
                    id={id}
                    {...describedBy}
                    aria-label="Spending limit"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unset">Not set</SelectItem>
                    <SelectItem value="unlimited">Unlimited</SelectItem>
                    <SelectItem value="limited">Limited amount</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </SettingRow>
            {draft.limitState === "limited" && (
              <FormField
                label={`Limit amount (${controls.currency}${usesMinorUnits ? " minor units" : ""})`}
                description={
                  usesMinorUnits
                    ? "Enter an exact whole number of minor units. Currency decimal formatting is unavailable."
                    : `Enter an exact ${controls.currency} amount${fractionDigits === 0 ? " with no decimal places" : ` with up to ${fractionDigits} decimal places`}.`
                }
                error={editor.amountError}
              >
                {(ids) => (
                  <Input
                    {...ids}
                    inputMode={
                      usesMinorUnits || fractionDigits === 0
                        ? "numeric"
                        : "decimal"
                    }
                    autoComplete="off"
                    maxLength={40}
                    value={draft.amount}
                    disabled={editor.blocked}
                    onChange={(event) =>
                      editor.changeDraft({
                        ...draft,
                        amount: event.target.value,
                      })
                    }
                  />
                )}
              </FormField>
            )}
            {feedback === "conflict" && (
              <Alert variant="warning">
                <AlertTitle>Settings changed elsewhere</AlertTitle>
                <AlertDescription>
                  Your draft has not been applied. Load current settings and
                  review them before making a new edit.
                </AlertDescription>
                <Button
                  variant="secondary"
                  className="mt-3"
                  onClick={() => void editor.load()}
                >
                  Discard draft and load current settings
                </Button>
              </Alert>
            )}
            {feedback === "not-configured" && (
              <Alert variant="warning">
                <AlertTitle>Commercial enrollment is unavailable</AlertTitle>
                <AlertDescription>
                  These controls can only edit existing enrollment. Reload
                  settings to check the organization's current enrollment.
                </AlertDescription>
                <Button
                  variant="secondary"
                  className="mt-3"
                  onClick={() => void editor.load()}
                >
                  Reload settings
                </Button>
              </Alert>
            )}
            {feedback === "uncertain" && (
              <Alert variant="warning">
                <AlertTitle>Save outcome is uncertain</AlertTitle>
                <AlertDescription>
                  Your edit is locked so the same request can be retried safely.
                  Retry to check whether it was accepted.
                </AlertDescription>
                <Button
                  variant="secondary"
                  className="mt-3"
                  disabled={editor.isSaving}
                  onClick={() => void editor.save()}
                >
                  Retry the same save
                </Button>
              </Alert>
            )}
            {feedback === "rejected" && (
              <Alert variant="danger">
                <AlertTitle>Settings were not saved</AlertTitle>
                <AlertDescription>
                  Check your organization access and entered values, then retry
                  saving.
                </AlertDescription>
              </Alert>
            )}
            {(feedback === "saved" || feedback === "superseded") && (
              <p role="status" className="text-sm text-coval-text-muted">
                {feedback === "saved"
                  ? "Settings saved."
                  : "Your request was accepted. A newer edit is displayed."}
              </p>
            )}
            <FormActions>
              <SaveResourceButton
                type="submit"
                resourceName="overage settings"
                loading={editor.isSaving}
                disabled={editor.blocked || !editor.hasChanges}
                disabledReason={
                  editor.pendingRequest
                    ? "Retry the same save to resolve the uncertain outcome."
                    : editor.blocked
                      ? "Load and review current settings before saving."
                      : !editor.hasChanges
                        ? "Make a change before saving."
                        : undefined
                }
                validationIssues={editor.validationIssues}
                onValidationAttempt={editor.revealValidation}
              />
            </FormActions>
          </form>
        )}
      </CardContent>
    </Card>
  )
}

export function OverageSettingsPanel(props: OverageSettingsPanelProps) {
  return <OverageSettingsEditor key={props.organizationId} {...props} />
}
